/*
 * SETUP REQUIRED BEFORE LAUNCH
 * Set INQUIRY_ENDPOINT to a secure server-side endpoint (for example a Supabase Edge Function)
 * that validates submissions, stores them in an access-restricted table, stores CVs in a
 * PRIVATE storage bucket, and notifies the team. Never place API keys or service-role
 * credentials in this file — the endpoint must hold them server-side.
 * Until configured, forms will NOT claim success; they offer an email draft instead.
 */
export const INQUIRY_ENDPOINT = '';

export type InquiryType = 'employer' | 'candidate' | 'general';

export type SubmitResult =
  | { ok: true }
  | { ok: false; reason: 'not_configured' | 'network' | 'server'; message: string };

export async function submitInquiry(
  type: InquiryType,
  values: Record<string, string | boolean>,
  file?: File | null,
): Promise<SubmitResult> {
  if (!INQUIRY_ENDPOINT) {
    return {
      ok: false,
      reason: 'not_configured',
      message: 'Online submissions are not connected yet, so nothing has been sent.',
    };
  }

  const body = new FormData();
  body.append('inquiryType', type);
  Object.entries(values).forEach(([key, value]) => body.append(key, String(value)));
  if (file) body.append('cv', file);

  try {
    const response = await fetch(INQUIRY_ENDPOINT, { method: 'POST', body });
    if (!response.ok) {
      return {
        ok: false,
        reason: 'server',
        message: 'We could not submit your inquiry just now. Please try again, or email a consultant directly.',
      };
    }
    return { ok: true };
  } catch {
    return {
      ok: false,
      reason: 'network',
      message: 'We could not reach our servers. Please check your connection and try again.',
    };
  }
}

export function buildMailto(to: string[], subject: string, lines: string[]): string {
  const params = new URLSearchParams({ subject, body: lines.join('\n') });
  return `mailto:${to.join(',')}?${params.toString().replace(/\+/g, '%20')}`;
}
